import { Injectable, inject } from '@angular/core';
import { arrayAdd, arrayRemove, arrayUpdate, Store, StoreConfig } from '@datorama/akita';
import { AusErrorLogModel, DesktopUnderwritingSystemTypes, OrderLogModel, VerificationServiceModel } from '../../aus/models';
import { AusModel, AusValidationIssue, Cmg1003Model } from '../../cmg-1003/models';
import { SessionStore } from '../../core/state';
import { iStoreActions } from '../core/store-actions.interface';

function createInitialState(): AusModel {
  return <AusModel>{};
}

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'cmg-aus-store' })
export class AusStore extends Store<AusModel> implements iStoreActions<Cmg1003Model> {
  private readonly _sessionStore = inject(SessionStore);

  private _initValue: AusModel = null;
  constructor() {
    super(createInitialState());
  }

  override akitaPreUpdate(_: Readonly<AusModel>, nextState: Readonly<AusModel>): AusModel {
    this._sessionStore.update({ lastUpdatedDateTime: Date.now() });
    return nextState;
  }

  preInitialize(all1003Model: Cmg1003Model): void {
    this.update(all1003Model?.aus);
    this.update({ fileDataId: all1003Model?.fileDataId });
    this.update({ ausSandboxConditionIndicator: all1003Model?.ausSandboxConditionIndicator });
    this._initValue = JSON.parse(JSON.stringify(this.getValue()));
  }

  get initialValue() {
    return this._initValue;
  }

  deleteVerificationService(vs: VerificationServiceModel) {
    this.update((ausModel: AusModel) => {
      return {
        ...ausModel,
        verificationServices:
          vs.serviceOrderId < 0
            ? arrayRemove(ausModel.verificationServices, vs.serviceOrderId, 'serviceOrderId')
            : arrayUpdate(ausModel.verificationServices, vs.serviceOrderId, { isDeleted: true }, 'serviceOrderId'),
      };
    });
  }

  updateVerificationService(vs: VerificationServiceModel) {
    const stateFunction = (ausModel: AusModel) => {
      if (
        vs.serviceOrderId < 0 ||
        ausModel?.verificationServices?.length === 0 ||
        ausModel?.verificationServices?.every((curVs) => curVs.serviceOrderId !== vs.serviceOrderId)
      ) {
        return {
          ...ausModel,
          verificationServices: arrayAdd(ausModel.verificationServices, vs),
        };
      } else {
        return {
          ...ausModel,
          updatedDateTime: new Date(), // sets the store dirty since the actual update in the line below is not doing it!
          verificationServices: [...arrayUpdate(ausModel.verificationServices, vs.serviceOrderId, vs, 'serviceOrderId')],
        };
      }
    };

    this.update(stateFunction);
  }

  addOrderLog(orderLog: OrderLogModel) {
    const stateFunction = (ausModel: AusModel) => {
      return {
        ...ausModel,
        orderLogs: arrayAdd(ausModel.orderLogs, orderLog),
      };
    };
    this.update(stateFunction);
  }

  addValidations(system: DesktopUnderwritingSystemTypes, validationIssues: AusValidationIssue[]) {
    let index = 0;
    const errorLogs: AusErrorLogModel[] = [];
    (validationIssues || []).forEach((t) => {
      errorLogs.push({
        id: --index,
        title: '',
        message: (t.category ? `${t.category}: ` : '') + (!t.message ? t.fieldName : t.message),
        system: system === 1 ? 'DU' : 'LPA',
      });
    });
    this.updateErrorLog(errorLogs);
  }

  addErrors(errors: string[]) {
    let index = 0;
    const errorLogs: AusErrorLogModel[] = [];
    (errors || []).forEach((t) => {
      errorLogs.push({
        id: --index,
        title: '',
        message: t,
        system: 'DU',
      });
    });
    this.updateErrorLog(errorLogs);
  }

  clearErrorLogs() {
    const stateFunction = (ausModel: AusModel) => {
      return {
        ...ausModel,
        errorLogs: [],
      };
    };
    this.update(stateFunction);
  }

  updateErrorLog(errorLogs: AusErrorLogModel[]) {
    const stateFunction = (ausModel: AusModel) => {
      return {
        ...ausModel,
        errorLogs: arrayAdd(ausModel.errorLogs, errorLogs),
      };
    };
    this.update(stateFunction);
  }
}
